import { runTransaction, getFirestore } from 'firebase/firestore'
import templateService from '../../services/templateService'
import { Template } from '@leadgen/models/client'
import config from '../../config'
import companyService from '../../services/companyService'
import { defaultTemplateId } from '../../constants/defaultTemplateId'

class TemplateForm {
  onSubmitCreate = async ({
    pdf,
    templateJSON,
    companyId,
  }: {
    pdf: Blob
    templateJSON: Blob
    companyId: string
  }): Promise<Template> => {
    const url = `${config.bucketBaseUrl}${encodeURIComponent(`templates/${companyId}/${defaultTemplateId}_1.pdf`)}?alt=media`
    const templateJSONUrl = `${config.bucketBaseUrl}${encodeURIComponent(`templates/${companyId}/${defaultTemplateId}_1.json`)}?alt=media`
    let uploadFileError: Error
    const template: Template = await runTransaction(getFirestore(), async transaction => {
      const template = templateService.createTemplate({
        transactionOrBatch: transaction,
        companyId,
        template: {
          url,
          templateJSONUrl,
          version: 1,
          generatingPreview: true,
          approved: false,
        },
      }) as Template
      companyService.updateLastChangedTemplate({
        transactionOrBatch: transaction,
        companyId,
        lastChangedTemplateVersion: 1,
      })
      await templateService.uploadPdf({
        pdf,
        companyId,
        version: 1,
      })
      await templateService
        .uploadTemplateJSON({
          templateJSON,
          companyId,
          version: 1,
        })
        .catch((error: Error) => {
          uploadFileError = error
          throw error
        })
      return template
    }).catch((error: Error) => {
      // if for some reason the transaction fails after our code successfully uploaded the pdf to storage, the backend process will inevitably create the missing document with the same id if it doesnt exist. so we continue down the happy path
      // the main reason for creating the template doc here is to try cover the few second delay between the pdf being uploaded and the doc being created on the server during which the user may refresh the page to see a fresh templates list which should include this template if possible
      if (!uploadFileError) {
        const template: Template = {
          id: defaultTemplateId,
          url,
          templateJSONUrl,
          version: 1,
          generatingPreview: true,
          approved: false,
        }
        return template
      }
      throw error
    })
    return template
  }
  onSubmitUpdate = async ({
    pdf,
    templateJSON,
    companyId,
  }: {
    pdf: Blob
    templateJSON: Blob
    companyId: string
  }): Promise<number> => {
    return runTransaction(getFirestore(), async transaction => {
      // if something updates the template we want it to retry the transaction. uploading the pdf is idempotent so its fine. it will however trigger multiple storage function onObjectFinalized calls which will all wait til the firestore template document version is at least as high as the one on the file metadata and settle down
      const [company, template] = await Promise.all([
        companyService.fetchCompany({ transaction, companyId }),
        templateService.fetchTemplate({ transaction, companyId }),
      ])
      const version = template.version + 1
      if (company.pendingReviewTemplateVersion !== null) {
        companyService.updateCompanyPendingReviewTemplate({
          transactionOrBatch: transaction,
          companyId,
          pendingReviewTemplateVersion: version,
        })
      }
      templateService.updateTemplateVersion({
        transactionOrBatch: transaction,
        companyId,
        version,
      })
      companyService.updateLastChangedTemplate({
        transactionOrBatch: transaction,
        companyId,
        lastChangedTemplateVersion: version,
      })
      await templateService.uploadPdf({
        pdf,
        companyId,
        version,
      })
      await templateService.uploadTemplateJSON({
        templateJSON,
        companyId,
        version,
      })
      return version
    })
  }
}

export default new TemplateForm()
